/*
 * ContentRefreshCheck
 * Applies tip 15: only show a new date when the content has really been updated.
 *
 * Rules (from what the reader ticks):
 * - SUBSTANTIVE = law/statute change, new section, corrected facts.
 * - REVIEWED    = an attorney re-read the page and confirmed it is accurate.
 * - MINOR       = links fixed, typos/formatting only.
 * - DATE_ONLY   = nothing changed except the date.
 * Verdict:
 *   any SUBSTANTIVE                 -> change "Updated" date AND "Last reviewed by" line
 *   else REVIEWED                    -> change "Last reviewed by" line only, keep "Updated" date
 *   else any MINOR                   -> keep both dates
 *   else DATE_ONLY                   -> keep both dates (warning: this is a fake refresh)
 *   nothing ticked                   -> no result yet
 * DATE_ONLY is exclusive: ticking it clears the others, and ticking anything else clears it.
 * No tracking, storage or network calls.
 */
import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";

type Change = "law" | "section" | "facts" | "reviewed" | "links" | "typos" | "dateOnly";

const CHANGES: { key: Change; label: string }[] = [
  { key: "law", label: "A law, statute or case it covers has changed, and the page now reflects it" },
  { key: "section", label: "Added a new section that answers another real client question" },
  { key: "facts", label: "Corrected facts, figures, deadlines or procedures" },
  { key: "reviewed", label: "An attorney re-read it and confirmed it's still accurate" },
  { key: "links", label: "Fixed or updated links" },
  { key: "typos", label: "Fixed typos or formatting only" },
  { key: "dateOnly", label: "Nothing changed. I just want a newer date on it" },
];

const SUBSTANTIVE: Change[] = ["law", "section", "facts"];
const MINOR: Change[] = ["links", "typos"];

export default function ContentRefreshCheck() {
  const [ticked, setTicked] = useState<Record<Change, boolean>>({
    law: false,
    section: false,
    facts: false,
    reviewed: false,
    links: false,
    typos: false,
    dateOnly: false,
  });

  const toggle = (key: Change) =>
    setTicked((prev) => {
      if (key === "dateOnly") {
        const next = !prev.dateOnly;
        return { law: false, section: false, facts: false, reviewed: false, links: false, typos: false, dateOnly: next };
      }
      return { ...prev, [key]: !prev[key], dateOnly: false };
    });

  const substantive = SUBSTANTIVE.some((k) => ticked[k]);
  const minor = MINOR.some((k) => ticked[k]);
  const any = Object.values(ticked).some(Boolean);

  let result: { title: string; body: string } | null = null;
  if (substantive) {
    result = {
      title: "Update both dates.",
      body: "Change the “Updated” date and the “Last reviewed by [attorney]” line. If you can, add a short note on what changed so readers (and your team) can see why.",
    };
  } else if (ticked.reviewed) {
    result = {
      title: "Update the “Last reviewed by” line only.",
      body: "A real attorney review is worth showing, but the content itself hasn't changed, so keep the “Updated” date as it is.",
    };
  } else if (minor) {
    result = {
      title: "Keep both dates as they are.",
      body: "Fixing links and typos is maintenance, not an update. Make the fixes and leave the dates alone.",
    };
  } else if (ticked.dateOnly) {
    result = {
      title: "Don't change the dates.",
      body: "A new date with no real change is the fake refresh tip 15 warns about. If the page is out of date, update the content first, then the date.",
    };
  }

  const focusRing =
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

  return (
    <section className="not-prose my-8 rounded-xl border border-border bg-card p-5 text-card-foreground sm:p-6">
      <h3 className="text-lg font-semibold">Should this page get a new date?</h3>
      <p className="mt-1 text-sm text-muted-foreground">
        Think of one older page or post. Tick everything you actually changed.
      </p>

      <fieldset className="mt-4">
        <legend className="sr-only">What changed on the page</legend>
        <ul className="space-y-2">
          {CHANGES.map((c) => (
            <li key={c.key}>
              <Button variant="outline"
                type="button"
                aria-pressed={ticked[c.key]}
                onClick={() => toggle(c.key)}
                className={`h-auto w-full justify-start whitespace-normal rounded-lg border px-3 py-2 text-left text-sm ${focusRing} ${
                  ticked[c.key] ? "border-border bg-primary text-primary-foreground" : "border-input bg-card text-card-foreground"
                }`}
              >
                <span aria-hidden="true">{ticked[c.key] ? "✓ " : "○ "}</span>
                {c.label}
              </Button>
            </li>
          ))}
        </ul>
      </fieldset>

      <div aria-live="polite" className="mt-5 rounded-lg bg-muted p-4">
        {!any || !result ? (
          <p className="text-sm text-muted-foreground">Your answer will appear here.</p>
        ) : (
          <div>
            <p className="text-sm font-semibold">{result.title}</p>
            <p className="mt-1 text-sm">{result.body}</p>
            <p className="mt-3 text-sm">
              Need attorney-reviewed updates done properly?{" "}
              <Link to="/services/$slug" params={{ slug: "legal-content-writing" }} className={`font-medium underline underline-offset-4 ${focusRing}`}>
                Legal content writing →
              </Link>
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
